import { Inject, Injectable } from '@angular/core';
import { DocumentFeedbackModel } from '../components/document-feedback/document-feedback.model';
import { catchError, map, Observable, throwError } from 'rxjs';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    EnvironmentInstanceToken,
    Equipment,
    getSharpTicketUri,
    IncidentPriority,
    IncidentType,
    NotificationIdentifier,
    NotificationInterface,
    NumberUtilities,
    PaceEnvironment,
    SharpProviderIncidentResponse,
    SharpProviderResponse,
    UserInfo,
    UserMasterService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment from 'moment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NotificationInstanceToken } from '../core/interfaces/notification.interface';
import { DocumentFeedbackSharpPayload, DocumentSharpServiceNames } from '../types/document-feedback';

@Injectable({
    providedIn: 'root',
})
export class DocumentFeedbackService {
    public constructor(
        @Inject(EnvironmentInstanceToken) private readonly environment: PaceEnvironment,
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly userMasterService: UserMasterService,
        private readonly httpClient: HttpClient,
    ) {}

    public createSharpIncident(documentFeedbackModel: DocumentFeedbackModel): Observable<void> {
        const HTTP_OK = 200;

        const body = this.createPayload(documentFeedbackModel);
        const options = {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        };

        const uriCreateSharpTicket = getSharpTicketUri(this.environment.production, this.clientManager.isPacePWA());
        return this.sendPostRequest(`${this.environment.reportIssueUrl}${uriCreateSharpTicket}`, body, options).pipe(
            map((response: SharpProviderIncidentResponse[]) => {
                if (response.length === 0 || response[0].response?.statusLine?.statusCode !== HTTP_OK) {
                    throw new Error(`Sharp incident creation failed with error: ${JSON.stringify(response[0])}`);
                }
                this.log.info(`DocumentFeedbackService: createSharpIncident: SHARP ticket successfully created.`);
            }),
            catchError((error) => {
                this.log.error(
                    `DocumentFeedbackService: createSharpIncident: Error: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
                return throwError(() => new Error(error));
            }),
        );
    }

    private createPayload(documentFeedbackModal: DocumentFeedbackModel): DocumentFeedbackSharpPayload {
        const externalId = this.createExternalId(documentFeedbackModal.getGID());
        const priority = IncidentPriority.NOT_BLOCKED;
        const shortDescription = `Feedback for Form : ${documentFeedbackModal.form_id.value}`;
        const detailedDescription = this.getDocumentFeedbackDetailedDescription(documentFeedbackModal);
        const incidentType = <IncidentType>documentFeedbackModal.category.selectedEntry.value;
        const requester = new UserInfo(documentFeedbackModal.getGID(), '');

        return new DocumentFeedbackSharpPayload(
            externalId,
            this.getSharpServiceText(documentFeedbackModal.modality),
            priority,
            shortDescription,
            detailedDescription,
            incidentType,
            requester,
            requester,
            [],
        );
    }

    private getDocumentFeedbackDetailedDescription(documentFeedbackModal: DocumentFeedbackModel): string {
        const equipment = this.getMainEquipment(documentFeedbackModal.notification);
        return `
              Material number: ${equipment.matnr}

              Serial Number: ${equipment.sernr}

              Product Key: ${documentFeedbackModal.productKey}

              Docytpe Key: ${documentFeedbackModal.doctypeKey}

              Variant: ${documentFeedbackModal.variant}

              Version: ${documentFeedbackModal.version}

              Language: ${documentFeedbackModal.language}

              Modality: ${documentFeedbackModal.modality}

              Defaultinfoclass: ${documentFeedbackModal.defaultInfoclass}

              Description: ${documentFeedbackModal.feedback_description.value}

              Section number: ${documentFeedbackModal.section_number.value}
          `;
    }

    private createExternalId(gid: string): string {
        return `${gid}-${this.userMasterService.getSharpServiceText()}-${moment.now()}-${NumberUtilities.getRandomNumberBetweenMinAndMax()}`;
    }

    private getMainEquipment(notificationIdentifier: NotificationIdentifier): Equipment {
        const notification = this.notificationService.getNotificationStateByIdentifier(notificationIdentifier);
        if (typeof notification === 'undefined') {
            throw new Error(`DocumentFeedbackService : No notification available for document feedback`);
        }
        const equipment = this.notificationService.getMainEquipmentFromNotification(notification);
        if (typeof equipment === 'undefined') {
            throw new Error(
                `DocumentFeedbackService : No mainEquipment available on notification for document feedback`,
            );
        }
        return equipment;
    }

    private sendPostRequest(
        url: string,
        body: string | DocumentFeedbackSharpPayload,
        options: {
            headers?:
                | HttpHeaders
                | {
                      [header: string]: string | string[];
                  };
        },
    ): Observable<SharpProviderResponse> {
        return this.httpClient.post<SharpProviderResponse>(url, body, options);
    }

    private getSharpServiceText(key: string): DocumentSharpServiceNames {
        return (
            DocumentSharpServiceNames[key as keyof typeof DocumentSharpServiceNames] || DocumentSharpServiceNames.OTHER
        );
    }
}
